import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import {
  getRequirementDetails,
  fetchSpaceMemberCompliance,
  fetchCompanyMemberCompliance,
  classifyMemberCompliance
} from '@/lib/delegateComplianceService';
import {
  Users, Search, Loader2, Plus, Minus, Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

const COMPLIANCE_CONFIG = {
  expired: { label: 'Expired', style: 'bg-red-100 text-red-700 border-red-200' },
  missing: { label: 'Missing', style: 'bg-orange-100 text-orange-700 border-orange-200' },
  not_assigned: { label: 'Not Assigned', style: 'bg-amber-100 text-amber-700 border-amber-200' },
  expiring: { label: 'Expiring Soon', style: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  booked: { label: 'Booked', style: 'bg-blue-100 text-blue-700 border-blue-200' },
  valid: { label: 'Valid', style: 'bg-green-100 text-green-700 border-green-200' },
};

const ComplianceInfo = ({ compliance }) => {
  if (!compliance) return null;
  const config = COMPLIANCE_CONFIG[compliance.status];
  if (!config) return null;
  return (
    <Badge variant="outline" className={cn('text-[10px] py-0 px-1.5 shrink-0', config.style)}>
      {config.label}
    </Badge>
  );
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getProfileFromMember = (member) => {
  return member.user_profiles || member.user || {};
};

const getDisplayName = (member) => {
  const profile = getProfileFromMember(member);
  return profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown';
};

const MemberRow = ({
  member, onClick, showComplianceInfo, isRecommended, actionIcon: ActionIcon, actionClassName, disabled
}) => {
  const profile = getProfileFromMember(member);
  const compliance = member.compliance;

  return (
    <div
      onClick={disabled ? undefined : () => onClick(member)}
      className={cn(
        'flex items-center gap-2 px-3 py-2 transition-colors border-b last:border-0 group',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:bg-gray-100',
        isRecommended && !disabled && 'bg-orange-50/40 hover:bg-orange-100/60'
      )}
    >
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarImage src={profile.avatar_url} />
        <AvatarFallback className="text-[10px]">
          {getInitials(getDisplayName(member))}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">
          {getDisplayName(member)}
        </span>
      </div>
      {showComplianceInfo && compliance && (
        <ComplianceInfo compliance={compliance} />
      )}
      {!disabled && (
        <div className={cn(
          'shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
          actionClassName
        )}>
          <ActionIcon className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

const MemberSection = ({
  title, count, members, onMemberClick, showComplianceInfo, isRecommended, disabled, headerAction
}) => {
  if (count === 0) return null;

  return (
    <div>
      <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 bg-gray-50 border-b sticky top-0 z-10 flex items-center justify-between">
        <span>{title}</span>
        {headerAction}
      </div>

      {members.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          onClick={disabled ? null : onMemberClick}
          showComplianceInfo={showComplianceInfo}
          isRecommended={isRecommended}
          actionIcon={Plus}
          actionClassName="text-green-600"
          disabled={disabled}
        />
      ))}
    </div>
  );
};

const DelegatePicker = ({
  booking,
  assignedDelegates = [],
  onAdd,
  onRemove
}) => {
  const [spaceMembers, setSpaceMembers] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [complianceMap, setComplianceMap] = useState(new Map());
  const [requirementDetails, setRequirementDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filling, setFilling] = useState(false);
  const [fillingMessage, setFillingMessage] = useState('');

  // Capacity tracking
  const totalSpaces = parseInt(booking?.delegates) || 0;
  const assignedCount = assignedDelegates.filter(d => !d.unmatched).length;
  const availableSpaces = totalSpaces > 0 ? totalSpaces - assignedCount : Infinity;

  const fetchData = useCallback(async () => {
    if (!booking?.project_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Phase 1: Parallel fetch of members and requirement details
      const [spaceMembersRes, companyMembersRes, reqDetails] = await Promise.all([
        supabase
          .from('space_members')
          .select(`
            id, role, user_id,
            user_profiles!space_members_user_id_fkey (
              id, display_name, first_name, last_name, email, avatar_url
            )
          `)
          .eq('project_id', booking.project_id),

        booking.company_id
          ? supabase
              .from('company_members')
              .select(`
                id, user_id,
                user_profiles!inner (
                  id, display_name, first_name, last_name, email, avatar_url
                )
              `)
              .eq('company_id', booking.company_id)
          : { data: [], error: null },

        getRequirementDetails(booking)
      ]);

      const spaceData = spaceMembersRes.data || [];
      const companyData = companyMembersRes.data || [];

      setSpaceMembers(spaceData);
      setRequirementDetails(reqDetails);

      // Filter company members to exclude those already in space
      const spaceUserIds = new Set(spaceData.map(sm => sm.user_id));
      const companyOnly = companyData.filter(cm => !spaceUserIds.has(cm.user_id));
      setCompanyMembers(companyOnly);

      // Phase 2: Batch compliance fetch
      const spaceUserIdList = spaceData.map(sm => sm.user_id).filter(Boolean);
      const companyUserIdList = companyOnly.map(cm => cm.user_id).filter(Boolean);

      const [spaceCompliance, companyCompliance] = await Promise.all([
        reqDetails
          ? fetchSpaceMemberCompliance(booking.project_id, reqDetails.id, reqDetails.course_link_id, spaceUserIdList)
          : new Map(),
        reqDetails
          ? fetchCompanyMemberCompliance(reqDetails.id, reqDetails.course_link_id, companyUserIdList)
          : new Map()
      ]);

      // Merge into single map
      const merged = new Map([...spaceCompliance, ...companyCompliance]);
      setComplianceMap(merged);
    } catch (error) {
      console.error('DelegatePicker fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [booking?.id, booking?.project_id, booking?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fill from recommended handler
  const handleFillRecommended = useCallback(async (recommendedMembers) => {
    if (filling || availableSpaces <= 0) return;

    const toAssign = recommendedMembers.slice(0, availableSpaces);
    if (toAssign.length === 0) return;

    setFilling(true);
    setFillingMessage('Analysing recommendations...');

    try {
      // Brief delay for effect, then assign all in parallel
      await new Promise(resolve => setTimeout(resolve, 1000));
      await Promise.all(toAssign.map(member => onAdd(member)));
    } finally {
      setFilling(false);
      setFillingMessage('');
    }
  }, [filling, availableSpaces, onAdd]);

  // Grouping logic
  const groupedMembers = useMemo(() => {
    const assignedIds = new Set(
      assignedDelegates
        .filter(d => !d.unmatched)
        .map(d => d.space_member_id || d.id)
    );

    const availableSpace = spaceMembers.filter(sm => !assignedIds.has(sm.id));
    const availableCompany = companyMembers.filter(cm => !assignedIds.has(cm.id));

    const recommended = [];
    const inSpace = [];

    for (const sm of availableSpace) {
      const complianceRecord = complianceMap.get(sm.user_id);
      const compliance = classifyMemberCompliance(complianceRecord);
      const enriched = { ...sm, compliance };

      // Show as "recommended" if they need training:
      // - expired: had cert but it expired
      // - missing: assigned but no cert
      // - expiring: cert expires soon
      // - not_assigned: not assigned AND no valid cert
      if (compliance && ['expired', 'missing', 'expiring', 'not_assigned'].includes(compliance.status)) {
        recommended.push(enriched);
      } else {
        inSpace.push(enriched);
      }
    }

    const inCompany = availableCompany.map(cm => {
      const complianceRecord = complianceMap.get(cm.user_id);
      return {
        ...cm,
        compliance: classifyMemberCompliance(complianceRecord),
        _isCompanyMember: true
      };
    });

    // Sort recommended by priority (lower = higher priority)
    // This gives us: expired (0) → missing (1) → expiring (2.x based on days)
    recommended.sort((a, b) => {
      const priorityA = a.compliance?.priority ?? 99;
      const priorityB = b.compliance?.priority ?? 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // Secondary sort: for expired, sort by days (more negative = expired longer ago = higher priority)
      // For expiring, sort by days (fewer days = expires sooner = higher priority)
      const daysA = a.compliance?.daysUntilExpiry ?? 0;
      const daysB = b.compliance?.daysUntilExpiry ?? 0;
      return daysA - daysB;
    });

    // Apply search filter
    const filterFn = (member) => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      const profile = getProfileFromMember(member);
      const name = (profile.display_name || '').toLowerCase();
      const email = (profile.email || '').toLowerCase();
      const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.toLowerCase();
      return name.includes(s) || email.includes(s) || fullName.includes(s);
    };

    return {
      recommended: recommended.filter(filterFn),
      inSpace: inSpace.filter(filterFn),
      inCompany: inCompany.filter(filterFn),
    };
  }, [spaceMembers, companyMembers, assignedDelegates, complianceMap, search]);

  // If no requirement data, don't split into recommended — put all in "In Space"
  const hasRequirement = !!requirementDetails;

  const effectiveGroups = useMemo(() => {
    if (hasRequirement) return groupedMembers;
    // No requirement — merge recommended back into inSpace
    return {
      recommended: [],
      inSpace: [...groupedMembers.recommended, ...groupedMembers.inSpace],
      inCompany: groupedMembers.inCompany,
    };
  }, [hasRequirement, groupedMembers]);

  const totalAvailable =
    effectiveGroups.recommended.length +
    effectiveGroups.inSpace.length +
    effectiveGroups.inCompany.length;

  // Check if we've reached capacity
  const isFull = totalSpaces > 0 && assignedCount >= totalSpaces;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* LEFT PANEL: Assigned Delegates */}
      <div className="border rounded-lg flex flex-col min-h-[200px]">
        {/* Header with capacity */}
        <div className="px-3 py-2 border-b bg-gray-50/50 shrink-0 flex items-center justify-between">
          <span className="text-sm font-medium">Assigned</span>
          <span className={cn(
            'text-xs font-semibold',
            isFull ? 'text-green-600' :
            assignedCount > 0 ? 'text-blue-600' : 'text-gray-400'
          )}>
            {assignedCount}{totalSpaces > 0 ? ` / ${totalSpaces}` : ''}
          </span>
        </div>

        {/* Assigned delegates list */}
        <ScrollArea className="flex-1 max-h-[350px]">
          {assignedDelegates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400 px-4">
              <p className="text-sm">No delegates assigned</p>
            </div>
          ) : (
            assignedDelegates.map(delegate => (
              <div
                key={delegate.id}
                onClick={() => onRemove(delegate)}
                className="flex items-center gap-2 px-3 py-2 border-b last:border-0 hover:bg-red-50 cursor-pointer group transition-colors"
              >
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={delegate.avatar_url} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(delegate.display_name || delegate.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {delegate.display_name || delegate.name}
                  </p>
                </div>
                {delegate.unmatched && (
                  <Badge variant="destructive" className="text-[10px] py-0 shrink-0">
                    Unmatched
                  </Badge>
                )}
                <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500">
                  <Minus className="h-4 w-4" />
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </div>

      {/* RIGHT PANEL: Available to Assign */}
      <div className="border rounded-lg flex flex-col min-h-[200px]">
        {/* Header with search */}
        <div className="relative border-b shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search available members..."
            className="pl-9 h-9 text-sm border-0 rounded-none shadow-none focus-visible:ring-0"
          />
        </div>

        {/* Grouped member list */}
        <ScrollArea className="flex-1 max-h-[350px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : totalAvailable === 0 ? (
            <div className="px-4 py-12 text-center text-gray-400">
              <p className="text-sm font-medium">
                {search.trim() ? 'No matches found' : 'All members are assigned'}
              </p>
            </div>
          ) : (
            <>
              {/* Recommended Section */}
              <MemberSection
                title="Recommended"
                count={effectiveGroups.recommended.length}
                members={effectiveGroups.recommended}
                onMemberClick={onAdd}
                showComplianceInfo={true}
                isRecommended={true}
                disabled={isFull}
                headerAction={
                  !isFull && effectiveGroups.recommended.length > 0 && availableSpaces > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-5 px-1.5 text-[10px]",
                        filling
                          ? "text-purple-600 bg-purple-50 min-w-[140px]"
                          : "text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      )}
                      onClick={() => handleFillRecommended(effectiveGroups.recommended)}
                      disabled={filling}
                    >
                      {filling ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          <span className="truncate">{fillingMessage}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3 mr-0.5" />
                          Auto Assign
                        </>
                      )}
                    </Button>
                  ) : null
                }
              />

              {/* In Space Section */}
              <MemberSection
                title="In Space"
                count={effectiveGroups.inSpace.length}
                members={effectiveGroups.inSpace}
                onMemberClick={onAdd}
                showComplianceInfo={hasRequirement}
                disabled={isFull}
              />

              {/* In Company Section */}
              <MemberSection
                title="In Company"
                count={effectiveGroups.inCompany.length}
                members={effectiveGroups.inCompany}
                onMemberClick={onAdd}
                showComplianceInfo={hasRequirement}
                disabled={isFull}
              />
            </>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default DelegatePicker;
